import styles from './AdvancedSearchPage.module.css';

import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CrudPageLayout from 'components/bng/pages/common/layout/CrudPageLayout';
import useBimContext from 'components/hooks/useBimContext';
import BngSearch from 'components/bng/ui/BngSearch';
import Api from 'components/Api';
import Icon from 'components/ui/common/Icon';
import FilterDropdown from 'components/bng/pages/common/filter/FilterDropdown';
import bngYup from 'components/bng/form/yup/BngYup';
import FilterOpts from 'components/bng/pages/common/filter/FilterOpts';
import BngTable from 'components/bng/ui/BngTable';
import useTranslation from 'components/hooks/useTranslation';
import useAsyncEffect from 'components/hooks/useAsyncEffect';
import Utils from 'components/Utils';
import UiMsg from 'components/ui/UiMsg';
import BngIconButton from 'components/bng/ui/BngIconButton';

const buildTypeIcons = (msg) => {
  return [
    {
      name: msg.t('.analysis'),
      value: 'analysis',
      icon: '',
    },
    {
      name: msg.t('big.table'),
      value: 'bigtable',
      icon: '',
    },
    {
      name: msg.t('.dashboard'),
      value: 'dashboard',
      icon: '',
    },
    {
      name: msg.t('.kpi'),
      value: 'kpi',
      icon: '',
    },
    {
      name: msg.t('.newmap'),
      value: 'newmap',
      icon: '',
    },
  ];
};

export default function AdvancedSearchPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [objects, setObjects] = useState([]);
  const [tablesSortMode, setTablesSortMode] = useState([{ name: 'ASC' }]);
  const [filters, setFilters] = useState({ search: '', filterButton: { author: '', origin: '', type: [] } });
  const [loading, setLoading] = useState(false);
  const [groupedRowsExpansion, setGroupedRowsExpansion] = useState({});

  useAsyncEffect({
    async onMount() {
      setLoading(true);
      if (searchParams.size > 0) {
        setFilters({
          ...filters,
          search: decodeURI(searchParams.get('searchTerm')),
          filterButton: {
            ...filters.filterButton,
            type: JSON.parse(decodeURI(searchParams.get('selectedButtonFilters'))),
          },
        });
      }

      try {
        const data = await Api.AdvancedSearchApi.findItems();
        setObjects(data);
      } catch (e) {
        console.error('Error fetching data from AdvancedSearchPage', e);
        UiMsg.ajaxError(null, e);
      } finally {
        setLoading(false);
      }
    },
  });

  const authors = useMemo(() => _.uniq(objects.map((object) => object.creator)), [objects]);

  const origins = useMemo(() => _.uniq(objects.flatMap((object) => object.origins ?? [])), [objects]);

  const hasAnyFilter = useMemo(
    () =>
      filters.search !== '' ||
      filters.filterButton.type?.length > 0 ||
      filters.filterButton.origin !== '' ||
      filters.filterButton.author !== '',
    [filters]
  );

  const tableColumns = useMemo(() => {
    return [
      {
        key: 'none',
        label: '',
        sortable: false,
        rowClassName: styles.rowExtraInfos,
        size: 50,
        render(row) {
          if (_.isEmpty(row.dashObjects)) {
            return null;
          }

          return (
            <>
              <Icon
                icon={row.expanded ? 'remove' : 'add'}
                className={`${styles.expandIcon}`}
                onClick={(event) => {
                  event.preventDefault();
                  const clone = objects.slice();
                  const index = clone.findIndex((item) => item.path === row.path);
                  clone[index] = { ...row, expanded: !(row.expanded ?? false) };
                  setObjects(clone);
                }}
              />
              {row.expanded && <div className={`${styles.extraRowStyle}`} />}
            </>
          );
        },
      },
      {
        key: 'displayName',
        label: t('name'),
        sortable: true,
        render(row) {
          return (
            <div>
              <div className={`${styles.cellWrapper}`}>
                <Icon icon={row.icon} className={`${styles.Icon}`} />
                <div className={`${styles.regularCellStyle}`} title={row.displayName}>
                  <div className="object-name">
                    <a href={Api.buildUrl('/load.iface', { content: row.path })}>{row.displayName}</a>
                  </div>
                  {row.description}
                </div>
              </div>
              {row.expanded && (
                <div className={`${styles.extraRowStyle}`}>
                  <div className={!_.isEmpty(row.dashObjects) ? styles.dashObjectsWrapper : ''}>
                    {row.dashObjects.map((object) => {
                      return (
                        <div className={`${styles.dashObjectStyle}`}>
                          <Icon className={`${styles.Icon} row-extra-info-icon`} icon={object.icon} />
                          <a
                            href={Api.buildUrl('/load.iface', { content: object.path })}
                            className={`${styles.regularCellStyle} row-extra-info-icon dash-object-name`}
                            title={object.name}
                          >
                            {object.name}
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: 'origins',
        label: t('newInMemoryField'),
        rowClassName: styles.originsCellStyle,
        sortable: false,
        size: '15%',
        render(row) {
          return (
            <>
              <div className={`${styles.regularCellStyle} origins`}>
                {row.origins.map((origin) => (
                  <div className="origins-list-item">{origin}</div>
                ))}
              </div>
              {row.expanded && <div className={`${styles.extraRowStyle}`} />}
            </>
          );
        },
      },
      {
        key: 'creator',
        label: t('author'),
        sortable: true,
        size: '15%',
        render(row) {
          return (
            <>
              <div className={`${styles.regularCellStyle}`}>{row.creator}</div>
              {row.expanded && <div className={`${styles.extraRowStyle}`} />}
            </>
          );
        },
      },
      {
        key: 'createdAt',
        label: t('creation'),
        sortable: true,
        size: 130,
        render(row) {
          return (
            <>
              <div className={`${styles.regularCellStyle}`}>{Utils.Date.formatDateTime(row.createdAt)}</div>
              {row.expanded && <div className={`${styles.extraRowStyle}`} />}
            </>
          );
        },
      },
      {
        key: 'accesses',
        label: t('advanced.search.table.head.access'),
        sortable: true,
        size: 120,
        render(row) {
          return (
            <>
              <div className={`${styles.regularCellStyle}`}>{row.accesses}</div>
              {row.expanded && <div className={`${styles.extraRowStyle}`} />}
            </>
          );
        },
      },
    ];
  }, [objects]);

  const filteredRows = useMemo(() => {
    return filterTableRows(objects, filters, tablesSortMode[0]);
  }, [objects, filters, tablesSortMode]);

  const groupedRows = useMemo(() => {
    const rows = {};
    const groupExpansion = {};
    filteredRows.forEach((row) => {
      const type = Utils.Object.getObjectType(row.path);
      if (!rows[type]) {
        rows[type] = { type, rows: [] };
      }

      rows[type].rows.push({ ...row, expanded: row.expanded ?? (type === 'dashboard' && !_.isEmpty(row.dashObjects)) });
      if (!groupExpansion[type]) {
        groupExpansion[type] = groupedRowsExpansion[type] ?? true;
      }
    });
    setGroupedRowsExpansion(groupExpansion);

    Object.entries(rows).forEach(([key, value]) => {
      const sortMode = tablesSortMode.find((tsm) => key === tsm.type);
      value.rows = filterTableRows(value.rows, filters, sortMode);
    });

    return Object.keys(rows)
      .sort()
      .map((key) => rows[key]);
  }, [filteredRows]);

  const headerButtons = useMemo(() => {
    return (
      <div className={styles.headerButtonsWrapper}>
        <div className={styles.foundObjectsCount}>
          {filteredRows.length} {t('found.objects')}
        </div>
        <BngSearch
          value={filters.search}
          onChange={(val) => {
            setFilters({ ...filters, search: val });
          }}
          title={t('search.object.name')}
        />

        <ObjectFilterDropDown
          onChange={(values) => setFilters({ ...filters, filterButton: values })}
          initialValues={filters.filterButton}
          authors={authors}
          origins={origins}
        />
      </div>
    );
  }, [filters, filteredRows]);

  return (
    <CrudPageLayout
      pageTitle={t('advanced.search')}
      headerButtons={() => headerButtons}
      tableAllowHover={false}
      hasSearch={false}
      hasRefresh={false}
      loading={loading}
      tableRows={filteredRows}
      tableColumns={tableColumns}
      tableSortMode={tablesSortMode[0]}
      setTableSortMode={(sort) => {
        const clone = tablesSortMode.slice();
        clone[0] = sort;
        setTablesSortMode(clone);
      }}
    >
      {hasAnyFilter && !_.isEmpty(groupedRows) ? (
        <div className={`${styles.objectListWrapper}`}>
          {groupedRows.map((rowGroup, idx) => {
            const tableSortMode = tablesSortMode.find((tsm) => tsm.type === rowGroup.type);

            const open = groupedRowsExpansion[rowGroup.type] ?? true;
            return (
              <div key={idx} className={`${styles.objectListWrapper}`}>
                <div className={`${styles.typeQuantityWrapper}`}>
                  <BngIconButton
                    icon={open ? 'expand_less' : 'expand_more'}
                    onClick={() => {
                      const expansions = { ...groupedRowsExpansion };
                      expansions[rowGroup.type] = !open;
                      setGroupedRowsExpansion(expansions);
                    }}
                    className="mr-1"
                  />
                  <div className={`${styles.objectType}`}>{t(rowGroup.type)}</div>
                  <div className={`${styles.objectTypeQuantity}`}>{rowGroup.rows.length}</div>
                </div>
                <div className={`${styles.tableWrapper} ${!open ? 'hidden' : ''}`}>
                  <BngTable
                    rows={rowGroup.rows}
                    cols={tableColumns}
                    allowHover={false}
                    sortHandler={(props) => {
                      const name = props.currentSortMode.name;
                      const clone = tablesSortMode.slice();
                      const idx = clone.findIndex((c) => c.type === rowGroup.type);

                      const sortModeObject = {
                        type: rowGroup.type,
                        [props.col.key]: name === 'DESC' ? 'ASC' : props.currentSortMode.nextMode.name,
                      };

                      if (idx > 0) {
                        clone.splice(idx, 1, sortModeObject);
                      } else {
                        clone.push(sortModeObject);
                      }

                      setTablesSortMode(clone);
                    }}
                    sortMode={tableSortMode ? tableSortMode : { name: 'ASC' }}
                    stickyHeader
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </CrudPageLayout>
  );
}

function filterTableRows(objects = [], filters = {}, tableSortMode = {}) {
  const {
    search,
    filterButton: { origin, author, type },
  } = filters;

  let clonedObjects = objects.slice();

  if (search) {
    const lowerCaseSearch = search.toLowerCase();

    clonedObjects = clonedObjects.filter((object) => {
      return [
        object.displayName ?? '',
        object.description ?? '',
        ...(object.dashObjects?.map((o) => o.name ?? '') ?? []),
      ].some((n) => {
        return n.toLowerCase().includes(lowerCaseSearch);
      });
    });
  }

  if (author) {
    clonedObjects = clonedObjects.filter(
      (object) => object.creator.toLowerCase().replaceAll(' ', '') === author.toLowerCase().replaceAll(' ', '')
    );
  }

  if (origin) {
    clonedObjects = clonedObjects.filter((object) =>
      object.origins.find((o) => o.toLowerCase().replaceAll(' ', '') === origin.toLowerCase().replaceAll(' ', ''))
    );
  }

  if (type?.length > 0) {
    clonedObjects = clonedObjects.filter((object) => {
      const objectType = Utils.Object.getObjectType(object.path).toLowerCase();
      return type.some((n) => n.toLowerCase() === objectType);
    });
  }

  Object.entries(tableSortMode).forEach(([prop, direction]) => {
    if (direction === 'NONE') return;

    clonedObjects = _.orderBy(
      clonedObjects,
      [
        (object) => {
          const value = object[prop];
          return _.isString(value) ? value.toLowerCase() : value;
        },
      ],
      [direction.toLowerCase()]
    );
  });
  return clonedObjects;
}

const ObjectFilterDropDownSchema = bngYup((yup) => {
  return yup.object({
    origin: yup.string().nullable().default(''),
    author: yup.string().nullable().default(''),
    type: yup.array(yup.string()).nullable().default([]),
  });
});

function ObjectFilterDropDown({ onChange = _.noop, initialValues = {}, authors = [], origins = [] }) {
  const { msg } = useBimContext();

  const typeOpts = FilterOpts({
    options: buildTypeIcons(msg),
    hasIcon: false,
  });

  const authorsOpts = FilterOpts({
    options: authors.map((a) => ({ name: a || msg.t('without.information'), value: a || ' ' })),
    hasIcon: false,
  });

  const originsOpts = FilterOpts({
    options: origins.map((o) => ({ name: o || msg.t('without.information'), value: o || ' ' })),
    hasIcon: false,
  });

  const filterFields = [
    {
      name: 'origin',
      options: originsOpts,
    },
    {
      name: 'author',
      options: authorsOpts,
    },
    {
      name: 'type',
      options: typeOpts,
      multiple: true,
    },
  ];

  const initialFormValues = useMemo(() => _.merge({}, ObjectFilterDropDownSchema.default(), initialValues), [
    initialValues,
  ]);

  return (
    <FilterDropdown
      fields={filterFields}
      initialFormValues={initialFormValues}
      dropdownSchema={ObjectFilterDropDownSchema}
      onChange={onChange}
    />
  );
}
